// /opt/ptg01-v1/front-extranet/components/ExpenseQuickCreateSheet.tsx
// Sheet d'action "Nouvelle note de frais" — sélecteur de mode entre les 3 types.
//   - Mobile (< sm) : bottom sheet (slide depuis le bas)
//   - Desktop (≥ sm) : modal centré
// Contenu : 3 cards (Standard / Refacturable / Récurrente) avec leur icône, titre,
// tagline et couleur d'accent (gradient navy → blue-700 → teal).
// Click sur une card → navigue vers la destination correspondante puis ferme le sheet.
'use client';

import React, { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { X, CalendarCheck, ArrowRightLeft, Repeat, ChevronRight } from 'lucide-react';

interface ModeOption {
    key: 'standard' | 'refacturable' | 'recurrente';
    label: string;
    tagline: string;
    href: string;
    icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
    accent: string;
}

const MODE_OPTIONS: ModeOption[] = [
    {
        key: 'standard',
        label: 'Note de frais standard',
        tagline: 'Frais unique remboursé sur votre bulletin de salaire',
        href: '/depenses/creer',
        icon: CalendarCheck,
        accent: '#000091', // Nexvia navy
    },
    {
        key: 'refacturable',
        label: 'Note de frais refacturable',
        tagline: 'Frais facturé au client en sus du TJM',
        href: '/depenses/creer?refacturable=1',
        icon: ArrowRightLeft,
        accent: '#1e40af', // blue-700
    },
    {
        key: 'recurrente',
        label: 'Notes de frais récurrentes',
        tagline: 'Configuration des abonnements mensuels (loyer, internet…)',
        href: '/depenses?config=1',
        icon: Repeat,
        accent: '#0f766e', // teal-700
    },
];

interface Props {
    open: boolean;
    onClose: () => void;
}

export default function ExpenseQuickCreateSheet({ open, onClose }: Props) {
    const router = useRouter();

    // Lock scroll du body quand ouvert (pattern bottom sheet/modal)
    useEffect(() => {
        if (!open) return;
        const original = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = original; };
    }, [open]);

    // Esc pour fermer
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    function go(href: string) {
        onClose();
        router.push(href);
    }

    return (
        <>
            {/* Overlay — z-[60] pour passer au-dessus de la BottomTabBar (z-50). */}
            <button
                aria-label="Fermer"
                onClick={onClose}
                className="fixed inset-0 z-[60] bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-150"
            />

            {/* Sheet : bottom sheet sur mobile, modal centré sur sm+. z-[70] au-dessus de l'overlay. */}
            <div
                role="dialog"
                aria-modal="true"
                aria-label="Nouvelle note de frais"
                className={`
                    fixed z-[70] bg-white shadow-2xl
                    inset-x-0 bottom-0 rounded-t-2xl border-t-2 border-[#000091]/15
                    pb-[max(env(safe-area-inset-bottom),0.5rem)]
                    sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2
                    sm:w-[480px] sm:max-w-[calc(100vw-2rem)] sm:rounded-2xl sm:border-2 sm:border-[#000091]/25
                    sm:pb-0
                    animate-in slide-in-from-bottom sm:slide-in-from-bottom-0 sm:fade-in duration-200
                `}
            >
                {/* Drag handle (mobile uniquement, signalétique iOS/Android) */}
                <div className="sm:hidden flex justify-center pt-2 pb-1">
                    <div className="w-10 h-1 rounded-full bg-slate-300" />
                </div>

                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-[#000091]/10">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-[#000091]">Notes de frais</div>
                        <h2 className="text-[16px] font-black text-slate-900 leading-tight">Nouvelle note de frais</h2>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Fermer"
                        className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content — 3 modes en stack vertical avec leur couleur d'accent */}
                <div className="p-4 space-y-2 max-h-[70vh] sm:max-h-[600px] overflow-y-auto">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 px-1">
                        Choisir un type
                    </div>
                    {MODE_OPTIONS.map(opt => {
                        const Icon = opt.icon;
                        return (
                            <button
                                key={opt.key}
                                onClick={() => go(opt.href)}
                                className="w-full flex items-center gap-3 p-3 rounded-xl border-2 bg-white hover:bg-[var(--accent-hover)] active:scale-[0.99] transition-all text-left group"
                                style={{
                                    borderColor: `${opt.accent}25`,
                                    ['--accent-hover' as string]: `${opt.accent}08`,
                                } as React.CSSProperties}
                            >
                                <div
                                    className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                                    style={{
                                        backgroundColor: opt.accent,
                                        color: '#fff',
                                        boxShadow: `0 2px 6px ${opt.accent}40`,
                                    }}
                                >
                                    <Icon className="w-5 h-5" strokeWidth={2.25} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[13px] font-bold text-slate-900 leading-tight">{opt.label}</div>
                                    <div className="text-[11px] text-slate-500 leading-snug mt-0.5">{opt.tagline}</div>
                                </div>
                                <ChevronRight
                                    className="w-4 h-4 shrink-0 group-hover:translate-x-0.5 transition-transform"
                                    style={{ color: opt.accent }}
                                    strokeWidth={2.5}
                                />
                            </button>
                        );
                    })}
                </div>
            </div>
        </>
    );
}
